
import { useEffect, useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import { Stack, Typography, List, ListItem, Card, CardContent, CardHeader, Grid, Button, Chip, Box, IconButton, Tooltip, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, CircularProgress, Badge, Divider } from '@mui/material';

import BlockIcon from '@mui/icons-material/Block';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PeopleOutlineIcon from '@mui/icons-material/PeopleOutline';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import EditIcon from '@mui/icons-material/Edit';
import HistoryIcon from '@mui/icons-material/History';
import GavelIcon from '@mui/icons-material/Gavel';

import moment from 'moment';

import { RootState } from '../../store';
import { openSnackbar } from '../../store/slices/SnackbarSlice';

import { GetColorMap } from '../../helpers/GetColor';
import { formatCurrency } from '../../helpers/FormatCurrency';

import { DialogEditarExpediente } from '../../dialogs/DialogEditarExpediente';
import { DialogEditarExhortoRecibido } from '../../dialogs/DialogEditarExhortoRecibido';
import { DialogEditarIrregular } from '../../dialogs/DialogEditarIrregular';
import { DialogEditarAmparo } from '../../dialogs/DialogEditarAmparo';
import { DialogPartesExpediente } from '../../dialogs/DialogPartesExpediente';
import { DialogPartesExhortoRecibido } from '../../dialogs/DialogPartesExhortoRecibido';
import { DialogPartesIrregular } from '../../dialogs/DialogPartesIrregular';
import { DialogPartesAmparo } from '../../dialogs/DialogPartesAmparo';
import { DialogPrestacionesExpediente } from '../../dialogs/DialogPrestacionesExpediente';
import { DialogAgregarCitacionSentencia } from '../../dialogs/DialogAgregarCitacionSentencia';
import { DialogAutorizarExpedienteVirtual } from '../../dialogs/DialogAutorizarExpedienteVirtual';

import { ChipEstatus } from '../ChipEstatus';

import { CambiarEstatusExpedienteAutorizado, ObtenerAudienciasExpediente, ObtenerEtapasExpediente } from '../../connections/tribunal/ExpedienteVirtualConnection';

import { AmparoProps, AudienciaExpedienteProps, EtapaProps, ExhortoRecibidoProps, ExpedienteAutorizadoProps, ExpedienteProps, IrregularProps } from '../../interfaces/tribunal/ExpedienteVirtualInterface';

interface Props {
    expediente:     ExpedienteProps;
    exhorto:        ExhortoRecibidoProps;
    irregular:      IrregularProps;
    amparo:         AmparoProps;
}

export const ExpedienteVirtualInformacion = ({ expediente, exhorto, irregular, amparo }: Props) => {

    const dispatch = useDispatch();

    const { administrador, coordinador } = useSelector( ( state: RootState ) => state.auth );

    const [expedienteMostrado, setExpedienteMostrado] = useState<ExpedienteProps>( expediente );
    const [exhortoMostrado, setExhortoMostrado] = useState<ExhortoRecibidoProps>( exhorto );
    const [irregularMostrado, setIrregularMostrado] = useState<IrregularProps>( irregular );
    const [amparoMostrado, setAmparoMostrado] = useState<AmparoProps>( amparo );

    const [openEditarExpediente, setOpenEditarExpediente] = useState( false );
    const [openEditarExhorto, setOpenEditarExhorto] = useState( false );
    const [openEditarIrregular, setOpenEditarIrregular] = useState( false );
    const [openEditarAmparo, setOpenEditarAmparo] = useState( false );

    const [openDialogPartes, setOpenDialogPartes] = useState( false );
    const [openDialogPartesExhorto, setOpenDialogPartesExhorto] = useState( false );
    const [openDialogPartesIrregular, setOpenDialogPartesIrregular] = useState( false );
    const [openDialogPartesAmparo, setOpenDialogPartesAmparo] = useState( false );
    const [openDialogPrestaciones, setOpenDialogPrestaciones] = useState( false );
    const [openCitacionSentencia, setOpenCitacionSentencia] = useState( false );
    const [openAutorizarExpedienteVirtual, setOpenAutorizarExpedienteVirtual] = useState( false );

    const esExpediente = expedienteMostrado.idExpediente !== 0;
    const esExhorto     = exhortoMostrado.idExhortoRecibido !== 0;
    const esIrregular   = irregularMostrado.idIrregular !== 0;
    const esAmparo      = amparoMostrado.idAmparo !== 0;

    const [autorizados, setAutorizados] = useState<ExpedienteAutorizadoProps[]>( expedienteMostrado.expedienteAutorizados ?? [] );
    const [openAutorizados, setOpenAutorizados] = useState( false );

    const [openConfirmarEstatus, setOpenConfirmarEstatus] = useState( false );
    const [autorizadoSeleccionado, setAutorizadoSeleccionado] = useState<ExpedienteAutorizadoProps | null>( null );
    const [loadingEstatus, setLoadingEstatus] = useState( false );

    const [openAudiencias, setOpenAudiencias] = useState( false );
    const [loadingAudiencias, setLoadingAudiencias] = useState( false );
    const [audiencias, setAudiencias] = useState<AudienciaExpedienteProps[]>( [] );

    const [openEtapas, setOpenEtapas] = useState( false );
    const [loadingEtapas, setLoadingEtapas] = useState( false );
    const [etapas, setEtapas] = useState<EtapaProps[]>( [] );

    const coloresEstatusAudiencia = GetColorMap([
        'Audiencia programada',
        'Audiencia en proceso',
        'Audiencia celebrada',
        'Audiencia no celebrada',
        'Audiencia diferida',
        'Audiencia cancelada',
    ]);

    const coloresEstatusEtapa = GetColorMap(
        Array.from( new Set( [
            expedienteMostrado.estatus, expedienteMostrado.etapa,
            exhortoMostrado.estatus, exhortoMostrado.etapa,
            irregularMostrado.estatus, irregularMostrado.etapa,
            amparoMostrado.estatus,
        ].filter( Boolean ) ) )
    );

    const handleAbrirAudiencias = async () => {

        setOpenAudiencias( true );
        setLoadingAudiencias( true );

        await ObtenerAudienciasExpediente( expedienteMostrado.idExpediente ).then( resp => {

            setTimeout(() => {

                setAudiencias( resp );
                setLoadingAudiencias( false );

            }, 700);

        });

    }

    const handleAbrirEtapas = async ( params: { idExpediente?: number; idExhortoRecibido?: number; idIrregular?: number } ) => {

        setOpenEtapas( true );
        setLoadingEtapas( true );

        await ObtenerEtapasExpediente({
            idExpediente:       params.idExpediente ?? 0,
            idExhortoRecibido:  params.idExhortoRecibido ?? 0,
            idIrregular:        params.idIrregular ?? 0,
        }).then( resp => {

            setTimeout(() => {

                setEtapas( resp );
                setLoadingEtapas( false );

            }, 700);

        });

    }

    const handleAbrirCambioEstatus = ( autorizado: ExpedienteAutorizadoProps ) => {

        setAutorizadoSeleccionado( autorizado );
        setOpenConfirmarEstatus( true );

    }

    const handleCambiarEstatus = async () => {

        if( !autorizadoSeleccionado ) return;

        const activo = !autorizadoSeleccionado.activo;

        setLoadingEstatus( true );

        await CambiarEstatusExpedienteAutorizado({
            referencia:                 autorizadoSeleccionado.referencia,
            idExpedienteAutorizado:     autorizadoSeleccionado.idExpedienteAutorizado,
            activar:                    activo,
        }).then( resp => {

            setTimeout(() => {

                const { success, message } = resp;

                if( success ){

                    dispatch( openSnackbar({ message, variant: 'success' }) );

                    setAutorizados( prev => prev.map( (autorizado) =>
                        autorizado.idExpedienteAutorizado === autorizadoSeleccionado.idExpedienteAutorizado
                        ? { ...autorizado, activo }
                        : autorizado
                    ));

                    setOpenConfirmarEstatus( false );

                }
                else {

                    dispatch( openSnackbar({ message, variant: 'error' }) );

                }

                setLoadingEstatus( false );

            }, 700);

        });

    }

    useEffect(() => {

        setExpedienteMostrado( expediente );

    }, [ expediente ]);

    useEffect(() => {

        setExhortoMostrado( exhorto );

    }, [ exhorto ]);

    useEffect(() => {

        setIrregularMostrado( irregular );

    }, [ irregular ]);

    useEffect(() => {

        setAmparoMostrado( amparo );

    }, [ amparo ]);

    useEffect(() => {

        setAutorizados( expedienteMostrado.expedienteAutorizados ?? [] );

    }, [ expedienteMostrado.expedienteAutorizados ]);

    return (
        <Stack spacing={2}>

            {
                esExpediente
                &&
                    <Card>

                        <CardHeader
                            sx={{
                                backgroundColor: (theme) => theme.palette.primary.main,
                                py: 2,
                                '& .MuiCardHeader-action': { alignSelf: 'center', margin: 0 },
                            }}
                            subheader={<Typography variant='subtitle1' fontWeight={500} sx={{ color: 'white' }}> Información del expediente </Typography>}
                            action={
                                <Stack direction='row' spacing={1.5} alignItems='center'>

                                    {
                                        expedienteMostrado.estatus
                                        &&
                                            <ChipEstatus
                                                label={ 'Estatus' }
                                                value={ expedienteMostrado.estatus }
                                                color={ coloresEstatusEtapa[ expedienteMostrado.estatus ] ?? '#757575' }
                                            />
                                    }

                                    {
                                        ( administrador || coordinador )
                                        &&
                                            <Tooltip title='Editar expediente'>
                                                <IconButton
                                                    onClick={ () => setOpenEditarExpediente( true ) }
                                                    sx={{
                                                        color: 'white',
                                                        bgcolor: 'rgba(255, 255, 255, 0.15)',
                                                        '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.3)' },
                                                    }}
                                                >
                                                    <EditIcon />
                                                </IconButton>
                                            </Tooltip>
                                    }

                                    {
                                        ( administrador || coordinador )
                                        &&
                                            <Tooltip title='Agregar citación de sentencia'>
                                                <IconButton
                                                    onClick={ () => setOpenCitacionSentencia( true ) }
                                                    sx={{
                                                        color: 'white',
                                                        bgcolor: 'rgba(255, 255, 255, 0.15)',
                                                        '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.3)' },
                                                    }}
                                                >
                                                    <GavelIcon />
                                                </IconButton>
                                            </Tooltip>
                                    }

                                    {
                                        ( administrador || coordinador )
                                        &&
                                            <Tooltip title='Autorizar expediente virtual'>
                                                <IconButton
                                                    onClick={ () => setOpenAutorizarExpedienteVirtual( true ) }
                                                    sx={{
                                                        color: 'white',
                                                        bgcolor: 'rgba(255, 255, 255, 0.15)',
                                                        '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.3)' },
                                                    }}
                                                >
                                                    <PersonAddIcon />
                                                </IconButton>
                                            </Tooltip>
                                    }

                                    {
                                        autorizados.length !== 0
                                        &&
                                            <Tooltip title='Usuario(s) autorizado(s)'>
                                                <IconButton
                                                    onClick={ () => setOpenAutorizados( true ) }
                                                    sx={{
                                                        color: 'white',
                                                        bgcolor: 'rgba(255, 255, 255, 0.15)',
                                                        '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.3)' },
                                                    }}
                                                >
                                                    <Badge badgeContent={ autorizados.length } color='error'>
                                                        <PeopleOutlineIcon />
                                                    </Badge>
                                                </IconButton>
                                            </Tooltip>
                                    }

                                </Stack>
                            }
                        />

                        <CardContent>

                            <Grid container rowSpacing={3} columnSpacing={1}>

                                <Grid size={{ md: 4, xs: 12 }}>
                                    <Typography variant='subtitle2' color='text.secondary'>
                                        Número de expediente:
                                    </Typography>
                                </Grid>

                                <Grid size={{ md: 8, xs: 12 }}>
                                    <Typography variant='subtitle2'>
                                        { expedienteMostrado.numeroExpediente }
                                        { expedienteMostrado.numeroFolio && <small style={{ fontSize: 14 }}> (F. { expedienteMostrado.numeroFolio })</small> }
                                    </Typography>
                                </Grid>

                                {
                                    expedienteMostrado.numeroExpedientePrincipal
                                    &&
                                        <>
                                            <Grid size={{ md: 4, xs: 12 }}>
                                                <Typography variant='subtitle2' color='text.secondary'>
                                                    Expediente principal:
                                                </Typography>
                                            </Grid>

                                            <Grid size={{ md: 8, xs: 12 }}>
                                                <Typography variant='subtitle2'>
                                                    { expedienteMostrado.numeroExpedientePrincipal }
                                                </Typography>
                                            </Grid>
                                        </>
                                }

                                {
                                    expedienteMostrado.expedientesAcumulados.length !== 0
                                    &&
                                        <>
                                            <Grid size={{ md: 4, xs: 12 }}>
                                                <Typography variant='subtitle2' color='text.secondary'>
                                                    Expediente(s) acumulado(s):
                                                </Typography>
                                            </Grid>

                                            <Grid size={{ md: 8, xs: 12 }}>
                                                {
                                                    expedienteMostrado.expedientesAcumulados?.map( (element, i) => (
                                                        <Typography key={ i } variant='subtitle2'>
                                                            { element }
                                                        </Typography>
                                                    ))
                                                }
                                            </Grid>
                                        </>
                                }

                                {
                                    expedienteMostrado.fecha
                                    &&
                                        <>
                                            <Grid size={{ md: 4, xs: 12 }}>
                                                <Typography variant='subtitle2' color='text.secondary'>
                                                    Fecha:
                                                </Typography>
                                            </Grid>

                                            <Grid size={{ md: 8, xs: 12 }}>
                                                <Typography variant='subtitle2'>
                                                    { moment( expedienteMostrado.fecha ).format( 'DD-MM-YYYY HH:mm a' ) }
                                                </Typography>
                                            </Grid>
                                        </>
                                }

                                {
                                    expedienteMostrado.cuantia
                                    &&
                                        <>
                                            <Grid size={{ md: 4, xs: 12 }}>
                                                <Typography variant='subtitle2' color='text.secondary'>
                                                    Cuantía:
                                                </Typography>
                                            </Grid>

                                            <Grid size={{ md: 8, xs: 12 }}>
                                                <Typography variant='subtitle2'>
                                                    { formatCurrency( expedienteMostrado.cuantia ) }
                                                </Typography>
                                            </Grid>
                                        </>
                                }

                                {
                                    expedienteMostrado.tipoJuicio
                                    &&
                                        <>
                                            <Grid size={{ md: 4, xs: 12 }}>
                                                <Typography variant='subtitle2' color='text.secondary'>
                                                    Tipo de juicio:
                                                </Typography>
                                            </Grid>

                                            <Grid size={{ md: 8, xs: 12 }}>
                                                <Typography variant='subtitle2'>
                                                    { expedienteMostrado.tipoJuicio }
                                                </Typography>
                                            </Grid>
                                        </>
                                }

                                {
                                    expedienteMostrado.etapa
                                    &&
                                        <>
                                            <Grid size={{ md: 4, xs: 12 }}>
                                                <Typography variant='subtitle2' color='text.secondary'>
                                                    Etapa:
                                                </Typography>
                                            </Grid>

                                            <Grid size={{ md: 7, xs: 10 }}>
                                                <Typography variant='subtitle2'>
                                                    { expedienteMostrado.subEtapa ? `${ expedienteMostrado.etapa } (${ expedienteMostrado.subEtapa })` : expedienteMostrado.etapa }
                                                </Typography>
                                            </Grid>

                                            <Grid size={{ md: 1, xs: 2 }}>
                                                <Tooltip title='Ver etapas'>
                                                    <IconButton
                                                        size='small'
                                                        color='primary'
                                                        onClick={ () => handleAbrirEtapas({ idExpediente: expedienteMostrado.idExpediente }) }
                                                    >
                                                        <HistoryIcon fontSize='small' />
                                                    </IconButton>
                                                </Tooltip>
                                            </Grid>
                                        </>
                                }

                                <Grid size={{ md: 4, xs: 12 }}>
                                    <Typography variant='subtitle2' color='text.secondary'>
                                        Juez:
                                    </Typography>
                                </Grid>

                                <Grid size={{ md: 8, xs: 12 }}>
                                    <Typography variant='subtitle2'>
                                        { expedienteMostrado.juez }
                                    </Typography>
                                </Grid>

                                {
                                    expedienteMostrado.secretarioAdmisibilidad
                                    &&
                                        <>
                                            <Grid size={{ md: 4, xs: 12 }}>
                                                <Typography variant='subtitle2' color='text.secondary'>
                                                    Secretario(a) de admisibilidad:
                                                </Typography>
                                            </Grid>

                                            <Grid size={{ md: 8, xs: 12 }}>
                                                <Typography variant='subtitle2'>
                                                    { expedienteMostrado.secretarioAdmisibilidad }
                                                </Typography>
                                            </Grid>
                                        </>
                                }

                                {
                                    expedienteMostrado.secretarioTramite
                                    &&
                                        <>
                                            <Grid size={{ md: 4, xs: 12 }}>
                                                <Typography variant='subtitle2' color='text.secondary'>
                                                    Secretario(a) de trámite:
                                                </Typography>
                                            </Grid>

                                            <Grid size={{ md: 8, xs: 12 }}>
                                                <Typography variant='subtitle2'>
                                                    { expedienteMostrado.secretarioTramite }
                                                </Typography>
                                            </Grid>
                                        </>
                                }

                                {
                                    expedienteMostrado.secretarioTramiteMenor
                                    &&
                                        <>
                                            <Grid size={{ md: 4, xs: 12 }}>
                                                <Typography variant='subtitle2' color='text.secondary'>
                                                    Secretario(a) de trámite (menor cuantía):
                                                </Typography>
                                            </Grid>

                                            <Grid size={{ md: 8, xs: 12 }}>
                                                <Typography variant='subtitle2'>
                                                    { expedienteMostrado.secretarioTramiteMenor }
                                                </Typography>
                                            </Grid>
                                        </>
                                }

                                {
                                    expedienteMostrado.secretarioEjecucion
                                    &&
                                        <>
                                            <Grid size={{ md: 4, xs: 12 }}>
                                                <Typography variant='subtitle2' color='text.secondary'>
                                                    Secretario(a) de ejecución:
                                                </Typography>
                                            </Grid>

                                            <Grid size={{ md: 8, xs: 12 }}>
                                                <Typography variant='subtitle2'>
                                                    { expedienteMostrado.secretarioEjecucion }
                                                </Typography>
                                            </Grid>
                                        </>
                                }

                                <Grid size={{ md: 4, xs: 12 }}>
                                    <Typography variant='subtitle2' color='text.secondary'>
                                        Juzgado de origen:
                                    </Typography>
                                </Grid>

                                <Grid size={{ md: 8, xs: 12 }}>
                                    <Typography variant='subtitle2'>
                                        { expedienteMostrado.juzgadoOrigen }
                                    </Typography>
                                </Grid>

                                {
                                    ( expedienteMostrado.actor.length !== 0 || ( administrador || coordinador ) )
                                    &&
                                        <>
                                            <Grid size={{ md: 4, xs: 12 }}>
                                                <Typography variant='subtitle2' color='text.secondary'>
                                                    Actor(es):
                                                </Typography>
                                            </Grid>

                                            <Grid size={{ md: 7, xs: 10 }}>
                                                {
                                                    expedienteMostrado.actor?.map( (element, i) => (
                                                        <Typography key={ i } variant='subtitle2'>
                                                            { element }
                                                        </Typography>
                                                    ))
                                                }
                                            </Grid>

                                            <Grid size={{ md: 1, xs: 2 }}>
                                                {
                                                    ( administrador || coordinador )
                                                    &&
                                                        <Tooltip title='Editar partes'>
                                                            <IconButton
                                                                size='small'
                                                                color='primary'
                                                                onClick={ () => setOpenDialogPartes( true ) }
                                                            >
                                                                <EditIcon fontSize='small' />
                                                            </IconButton>
                                                        </Tooltip>
                                                }
                                            </Grid>
                                        </>
                                }

                                {
                                    ( expedienteMostrado.demandado.length !== 0 || ( administrador || coordinador ) )
                                    &&
                                        <>
                                            <Grid size={{ md: 4, xs: 12 }}>
                                                <Typography variant='subtitle2' color='text.secondary'>
                                                    Demandado(s):
                                                </Typography>
                                            </Grid>

                                            <Grid size={{ md: ( administrador || coordinador ) ? 7 : 8, xs: 10 }}>
                                                {
                                                    expedienteMostrado.demandado?.map( (element, i) => (
                                                        <Typography key={ i } variant='subtitle2'>
                                                            { element }
                                                        </Typography>
                                                    ))
                                                }
                                            </Grid>

                                            {
                                                ( administrador || coordinador )
                                                &&
                                                    <Grid size={{ md: 1, xs: 2 }}>
                                                        <Tooltip title='Editar partes'>
                                                            <IconButton
                                                                size='small'
                                                                color='primary'
                                                                onClick={ () => setOpenDialogPartes( true ) }
                                                            >
                                                                <EditIcon fontSize='small' />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </Grid>
                                            }
                                        </>
                                }

                                {
                                    expedienteMostrado.audiencias
                                    &&
                                        <>
                                            <Grid size={{ md: 4, xs: 12 }}>
                                                <Typography variant='subtitle2' color='text.secondary'>
                                                    Audiencias:
                                                </Typography>
                                            </Grid>

                                            <Grid size={{ md: 8, xs: 12 }}>
                                                <Button fullWidth variant='contained' size='small' onClick={ handleAbrirAudiencias }>
                                                    Ver audiencias
                                                </Button>
                                            </Grid>
                                        </>
                                }

                            </Grid>

                        </CardContent>

                    </Card>
            }

            {
                ( esExpediente && ( expedienteMostrado.prestaciones.length !== 0 || ( administrador || coordinador ) ) )
                &&
                    <Card>

                        <CardHeader
                            sx={{
                                backgroundColor: (theme) => theme.palette.primary.main,
                                py: 2,
                                '& .MuiCardHeader-action': { alignSelf: 'center', margin: 0 },
                            }}
                            subheader={<Typography variant='subtitle1' fontWeight={500} sx={{ color: 'white' }}> Prestación(es) </Typography>}
                            action={
                                ( administrador || coordinador )
                                &&
                                    <Tooltip title='Editar prestaciones'>
                                        <IconButton
                                            onClick={ () => setOpenDialogPrestaciones( true ) }
                                            sx={{
                                                color: 'white',
                                                bgcolor: 'rgba(255, 255, 255, 0.15)',
                                                '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.3)' },
                                            }}
                                        >
                                            <EditIcon />
                                        </IconButton>
                                    </Tooltip>
                            }
                        />

                        <CardContent>

                            <List dense>
                                {
                                    expedienteMostrado.prestaciones?.map( (item, index) => (
                                        <ListItem key={ index } disableGutters>
                                            <Typography variant='subtitle2'>
                                                { item }
                                            </Typography>
                                        </ListItem>
                                    ))
                                }
                            </List>

                        </CardContent>

                    </Card>
            }

            {
                esExhorto
                &&
                    <Card>

                        <CardHeader
                            sx={{
                                backgroundColor: (theme) => theme.palette.primary.main,
                                py: 2,
                                '& .MuiCardHeader-action': { alignSelf: 'center', margin: 0 },
                            }}
                            subheader={<Typography variant='subtitle1' fontWeight={500} sx={{ color: 'white' }}> Información del exhorto </Typography>}
                            action={
                                <Stack direction='row' spacing={1.5} alignItems='center'>

                                    {
                                        ( administrador || coordinador )
                                        &&
                                            <Tooltip title='Editar exhorto'>
                                                <IconButton
                                                    onClick={ () => setOpenEditarExhorto( true ) }
                                                    sx={{
                                                        color: 'white',
                                                        bgcolor: 'rgba(255, 255, 255, 0.15)',
                                                        '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.3)' },
                                                    }}
                                                >
                                                    <EditIcon />
                                                </IconButton>
                                            </Tooltip>
                                    }

                                    {
                                        exhortoMostrado.estatus
                                        &&
                                            <ChipEstatus
                                                label={ 'Estatus' }
                                                value={ exhortoMostrado.estatus }
                                                color={ coloresEstatusEtapa[ exhortoMostrado.estatus ] ?? '#757575' }
                                            />
                                    }

                                </Stack>
                            }
                        />

                        <CardContent>

                            <Grid container rowSpacing={3} columnSpacing={1}>

                                <Grid size={{ md: 4, xs: 12 }}>
                                    <Typography variant='subtitle2' color='text.secondary'>
                                        Número de exhorto:
                                    </Typography>
                                </Grid>

                                <Grid size={{ md: 8, xs: 12 }}>
                                    <Typography variant='subtitle2'>
                                        { exhortoMostrado.numeroExhorto }
                                        { exhortoMostrado.numeroFolio && <small style={{ fontSize: 14 }}> (F. { exhortoMostrado.numeroFolio })</small> }
                                    </Typography>
                                </Grid>

                                {
                                    exhortoMostrado.numeroExpedienteOrigen
                                    &&
                                        <>
                                            <Grid size={{ md: 4, xs: 12 }}>
                                                <Typography variant='subtitle2' color='text.secondary'>
                                                    Expediente de origen:
                                                </Typography>
                                            </Grid>

                                            <Grid size={{ md: 8, xs: 12 }}>
                                                <Typography variant='subtitle2'>
                                                    { exhortoMostrado.numeroExpedienteOrigen }
                                                </Typography>
                                            </Grid>
                                        </>
                                }

                                {
                                    exhortoMostrado.fecha
                                    &&
                                        <>
                                            <Grid size={{ md: 4, xs: 12 }}>
                                                <Typography variant='subtitle2' color='text.secondary'>
                                                    Fecha:
                                                </Typography>
                                            </Grid>

                                            <Grid size={{ md: 8, xs: 12 }}>
                                                <Typography variant='subtitle2'>
                                                    { moment( exhortoMostrado.fecha ).format( 'DD-MM-YYYY HH:mm a' ) }
                                                </Typography>
                                            </Grid>
                                        </>
                                }

                                {
                                    exhortoMostrado.cuantia
                                    &&
                                        <>
                                            <Grid size={{ md: 4, xs: 12 }}>
                                                <Typography variant='subtitle2' color='text.secondary'>
                                                    Cuantía:
                                                </Typography>
                                            </Grid>

                                            <Grid size={{ md: 8, xs: 12 }}>
                                                <Typography variant='subtitle2'>
                                                    { formatCurrency( exhortoMostrado.cuantia ) }
                                                </Typography>
                                            </Grid>
                                        </>
                                }

                                {
                                    exhortoMostrado.tipoJuicio
                                    &&
                                        <>
                                            <Grid size={{ md: 4, xs: 12 }}>
                                                <Typography variant='subtitle2' color='text.secondary'>
                                                    Tipo de juicio:
                                                </Typography>
                                            </Grid>

                                            <Grid size={{ md: 8, xs: 12 }}>
                                                <Typography variant='subtitle2'>
                                                    { exhortoMostrado.tipoJuicio }
                                                </Typography>
                                            </Grid>
                                        </>
                                }

                                {
                                    exhortoMostrado.etapa
                                    &&
                                        <>
                                            <Grid size={{ md: 4, xs: 12 }}>
                                                <Typography variant='subtitle2' color='text.secondary'>
                                                    Etapa:
                                                </Typography>
                                            </Grid>

                                            <Grid size={{ md: 7, xs: 10 }}>
                                                <Typography variant='subtitle2'>
                                                    { exhortoMostrado.subEtapa ? `${ exhortoMostrado.etapa } (${ exhortoMostrado.subEtapa })` : exhortoMostrado.etapa }
                                                </Typography>
                                            </Grid>

                                            <Grid size={{ md: 1, xs: 2 }}>
                                                <Tooltip title='Ver etapas'>
                                                    <IconButton
                                                        size='small'
                                                        color='primary'
                                                        onClick={ () => handleAbrirEtapas({ idExhortoRecibido: exhortoMostrado.idExhortoRecibido }) }
                                                    >
                                                        <HistoryIcon fontSize='small' />
                                                    </IconButton>
                                                </Tooltip>
                                            </Grid>
                                        </>
                                }

                                <Grid size={{ md: 4, xs: 12 }}>
                                    <Typography variant='subtitle2' color='text.secondary'>
                                        Juez:
                                    </Typography>
                                </Grid>

                                <Grid size={{ md: 8, xs: 12 }}>
                                    <Typography variant='subtitle2'>
                                        { exhortoMostrado.juez }
                                    </Typography>
                                </Grid>

                                {
                                    exhortoMostrado.secretario
                                    &&
                                        <>
                                            <Grid size={{ md: 4, xs: 12 }}>
                                                <Typography variant='subtitle2' color='text.secondary'>
                                                    Secretario(a) de trámite:
                                                </Typography>
                                            </Grid>

                                            <Grid size={{ md: 8, xs: 12 }}>
                                                <Typography variant='subtitle2'>
                                                    { exhortoMostrado.secretario }
                                                </Typography>
                                            </Grid>
                                        </>
                                }

                                <Grid size={{ md: 4, xs: 12 }}>
                                    <Typography variant='subtitle2' color='text.secondary'>
                                        Clase de exhorto:
                                    </Typography>
                                </Grid>

                                <Grid size={{ md: 8, xs: 12 }}>
                                    <Typography variant='subtitle2'>
                                        { exhortoMostrado.claseExhorto }
                                    </Typography>
                                </Grid>

                                <Grid size={{ md: 4, xs: 12 }}>
                                    <Typography variant='subtitle2' color='text.secondary'>
                                        Objeto del exhorto:
                                    </Typography>
                                </Grid>

                                <Grid size={{ md: 8, xs: 12 }}>
                                    <Typography variant='subtitle2'>
                                        { exhortoMostrado.objetoExhorto }
                                    </Typography>
                                </Grid>

                                {
                                    exhortoMostrado.autoridadExhortoRecibido
                                    &&
                                        <>
                                            <Grid size={{ md: 4, xs: 12 }}>
                                                <Typography variant='subtitle2' color='text.secondary'>
                                                    Autoridad del exhorto:
                                                </Typography>
                                            </Grid>

                                            <Grid size={{ md: 8, xs: 12 }}>
                                                <Typography variant='subtitle2'>
                                                    { exhortoMostrado.autoridadExhortoRecibido }
                                                </Typography>
                                            </Grid>
                                        </>
                                }

                                {
                                    exhortoMostrado.seNotifico
                                    &&
                                        <>
                                            <Grid size={{ md: 4, xs: 12 }}>
                                                <Typography variant='subtitle2' color='text.secondary'>
                                                    Fecha diligencia:
                                                </Typography>
                                            </Grid>

                                            <Grid size={{ md: 8, xs: 12 }}>
                                                <Typography variant='subtitle2'>
                                                    { moment( exhortoMostrado.fechaDiligencia ).format( 'DD-MM-YYYY HH:mm a' ) }
                                                </Typography>
                                            </Grid>
                                        </>
                                }

                                {
                                    exhortoMostrado.fechaDevolucion
                                    &&
                                        <>
                                            <Grid size={{ md: 4, xs: 12 }}>
                                                <Typography variant='subtitle2' color='text.secondary'>
                                                    Fecha de devolución:
                                                </Typography>
                                            </Grid>

                                            <Grid size={{ md: 8, xs: 12 }}>
                                                <Typography variant='subtitle2'>
                                                    { moment( exhortoMostrado.fechaDevolucion ).format( 'DD-MM-YYYY HH:mm a' ) }
                                                </Typography>
                                            </Grid>
                                        </>
                                }

                                <Grid size={{ md: 4, xs: 12 }}>
                                    <Typography variant='subtitle2' color='text.secondary'>
                                        Juzgado de origen:
                                    </Typography>
                                </Grid>

                                <Grid size={{ md: 8, xs: 12 }}>
                                    <Typography variant='subtitle2'>
                                        { exhortoMostrado.juzgadoOrigen }
                                    </Typography>
                                </Grid>

                                {
                                    ( exhortoMostrado.actor.length !== 0 || ( administrador || coordinador ) )
                                    &&
                                        <>
                                            <Grid size={{ md: 4, xs: 12 }}>
                                                <Typography variant='subtitle2' color='text.secondary'>
                                                    Actor(es):
                                                </Typography>
                                            </Grid>

                                            <Grid size={{ md: 7, xs: 10 }}>
                                                {
                                                    exhortoMostrado.actor?.map( (element, i) => (
                                                        <Typography key={ i } variant='subtitle2'>
                                                            { element }
                                                        </Typography>
                                                    ))
                                                }
                                            </Grid>

                                            <Grid size={{ md: 1, xs: 2 }}>
                                                {
                                                    ( administrador || coordinador )
                                                    &&
                                                        <Tooltip title='Editar partes'>
                                                            <IconButton
                                                                size='small'
                                                                color='primary'
                                                                onClick={ () => setOpenDialogPartesExhorto( true ) }
                                                            >
                                                                <EditIcon fontSize='small' />
                                                            </IconButton>
                                                        </Tooltip>
                                                }
                                            </Grid>
                                        </>
                                }

                                {
                                    ( exhortoMostrado.demandado.length !== 0 || ( administrador || coordinador ) )
                                    &&
                                        <>
                                            <Grid size={{ md: 4, xs: 12 }}>
                                                <Typography variant='subtitle2' color='text.secondary'>
                                                    Demandado(s):
                                                </Typography>
                                            </Grid>

                                            <Grid size={{ md: 7, xs: 10 }}>
                                                {
                                                    exhortoMostrado.demandado?.map( (element, i) => (
                                                        <Typography key={ i } variant='subtitle2'>
                                                            { element }
                                                        </Typography>
                                                    ))
                                                }
                                            </Grid>

                                            <Grid size={{ md: 1, xs: 2 }}>
                                                {
                                                    ( administrador || coordinador )
                                                    &&
                                                        <Tooltip title='Editar partes'>
                                                            <IconButton
                                                                size='small'
                                                                color='primary'
                                                                onClick={ () => setOpenDialogPartesExhorto( true ) }
                                                            >
                                                                <EditIcon fontSize='small' />
                                                            </IconButton>
                                                        </Tooltip>
                                                }
                                            </Grid>
                                        </>
                                }

                            </Grid>

                        </CardContent>

                    </Card>
            }

            {
                esIrregular
                &&
                    <Card>

                        <CardHeader
                            sx={{
                                backgroundColor: (theme) => theme.palette.primary.main,
                                py: 2,
                                '& .MuiCardHeader-action': { alignSelf: 'center', margin: 0 },
                            }}
                            subheader={<Typography variant='subtitle1' fontWeight={500} sx={{ color: 'white' }}> Información del irregular </Typography>}
                            action={
                                <Stack direction='row' spacing={1.5} alignItems='center'>

                                    {
                                        ( administrador || coordinador )
                                        &&
                                            <Tooltip title='Editar irregular'>
                                                <IconButton
                                                    onClick={ () => setOpenEditarIrregular( true ) }
                                                    sx={{
                                                        color: 'white',
                                                        bgcolor: 'rgba(255, 255, 255, 0.15)',
                                                        '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.3)' },
                                                    }}
                                                >
                                                    <EditIcon />
                                                </IconButton>
                                            </Tooltip>
                                    }

                                    {
                                        irregularMostrado.estatus
                                        &&
                                            <ChipEstatus
                                                label={ 'Estatus' }
                                                value={ irregularMostrado.estatus }
                                                color={ coloresEstatusEtapa[ irregularMostrado.estatus ] ?? '#757575' }
                                            />
                                    }

                                </Stack>
                            }
                        />

                        <CardContent>

                            <Grid container rowSpacing={3} columnSpacing={1}>

                                <Grid size={{ md: 4, xs: 12 }}>
                                    <Typography variant='subtitle2' color='text.secondary'>
                                        Número de irregular:
                                    </Typography>
                                </Grid>

                                <Grid size={{ md: 8, xs: 12 }}>
                                    <Typography variant='subtitle2'>
                                        { irregularMostrado.numeroIrregular }
                                    </Typography>
                                </Grid>

                                {
                                    irregularMostrado.numeroExpediente
                                    &&
                                        <>
                                            <Grid size={{ md: 4, xs: 12 }}>
                                                <Typography variant='subtitle2' color='text.secondary'>
                                                    Número de expediente:
                                                </Typography>
                                            </Grid>

                                            <Grid size={{ md: 8, xs: 12 }}>
                                                <Typography variant='subtitle2'>
                                                    { irregularMostrado.numeroExpediente }
                                                </Typography>
                                            </Grid>
                                        </>
                                }

                                {
                                    irregularMostrado.fecha
                                    &&
                                        <>
                                            <Grid size={{ md: 4, xs: 12 }}>
                                                <Typography variant='subtitle2' color='text.secondary'>
                                                    Fecha:
                                                </Typography>
                                            </Grid>

                                            <Grid size={{ md: 8, xs: 12 }}>
                                                <Typography variant='subtitle2'>
                                                    { moment( irregularMostrado.fecha ).format( 'DD-MM-YYYY HH:mm a' ) }
                                                </Typography>
                                            </Grid>
                                        </>
                                }

                                {
                                    irregularMostrado.tipoIrregular
                                    &&
                                        <>
                                            <Grid size={{ md: 4, xs: 12 }}>
                                                <Typography variant='subtitle2' color='text.secondary'>
                                                    Tipo de irregular:
                                                </Typography>
                                            </Grid>

                                            <Grid size={{ md: 8, xs: 12 }}>
                                                <Typography variant='subtitle2'>
                                                    { irregularMostrado.tipoIrregular }
                                                </Typography>
                                            </Grid>
                                        </>
                                }

                                {
                                    irregularMostrado.etapa
                                    &&
                                        <>
                                            <Grid size={{ md: 4, xs: 12 }}>
                                                <Typography variant='subtitle2' color='text.secondary'>
                                                    Etapa:
                                                </Typography>
                                            </Grid>

                                            <Grid size={{ md: 7, xs: 10 }}>
                                                <Typography variant='subtitle2'>
                                                    { irregularMostrado.subEtapa ? `${ irregularMostrado.etapa } (${ irregularMostrado.subEtapa })` : irregularMostrado.etapa }
                                                </Typography>
                                            </Grid>

                                            <Grid size={{ md: 1, xs: 2 }}>
                                                <Tooltip title='Ver etapas'>
                                                    <IconButton
                                                        size='small'
                                                        color='primary'
                                                        onClick={ () => handleAbrirEtapas({ idIrregular: irregularMostrado.idIrregular }) }
                                                    >
                                                        <HistoryIcon fontSize='small' />
                                                    </IconButton>
                                                </Tooltip>
                                            </Grid>
                                        </>
                                }

                                <Grid size={{ md: 4, xs: 12 }}>
                                    <Typography variant='subtitle2' color='text.secondary'>
                                        Juez:
                                    </Typography>
                                </Grid>

                                <Grid size={{ md: 8, xs: 12 }}>
                                    <Typography variant='subtitle2'>
                                        { irregularMostrado.juez }
                                    </Typography>
                                </Grid>

                                {
                                    irregularMostrado.secretario
                                    &&
                                        <>
                                            <Grid size={{ md: 4, xs: 12 }}>
                                                <Typography variant='subtitle2' color='text.secondary'>
                                                    Secretario(a) de trámite:
                                                </Typography>
                                            </Grid>

                                            <Grid size={{ md: 8, xs: 12 }}>
                                                <Typography variant='subtitle2'>
                                                    { irregularMostrado.secretario }
                                                </Typography>
                                            </Grid>
                                        </>
                                }

                                <Grid size={{ md: 4, xs: 12 }}>
                                    <Typography variant='subtitle2' color='text.secondary'>
                                        Juzgado de origen:
                                    </Typography>
                                </Grid>

                                <Grid size={{ md: 8, xs: 12 }}>
                                    <Typography variant='subtitle2'>
                                        { irregularMostrado.juzgadoOrigen }
                                    </Typography>
                                </Grid>

                                {
                                    ( irregularMostrado.actor.length !== 0 || ( administrador || coordinador ) )
                                    &&
                                        <>
                                            <Grid size={{ md: 4, xs: 12 }}>
                                                <Typography variant='subtitle2' color='text.secondary'>
                                                    Actor(es):
                                                </Typography>
                                            </Grid>

                                            <Grid size={{ md: 7, xs: 10 }}>
                                                {
                                                    irregularMostrado.actor?.map( (element, i) => (
                                                        <Typography key={ i } variant='subtitle2'>
                                                            { element }
                                                        </Typography>
                                                    ))
                                                }
                                            </Grid>

                                            <Grid size={{ md: 1, xs: 2 }}>
                                                {
                                                    ( administrador || coordinador )
                                                    &&
                                                        <Tooltip title='Editar partes'>
                                                            <IconButton
                                                                size='small'
                                                                color='primary'
                                                                onClick={ () => setOpenDialogPartesIrregular( true ) }
                                                            >
                                                                <EditIcon fontSize='small' />
                                                            </IconButton>
                                                        </Tooltip>
                                                }
                                            </Grid>
                                        </>
                                }

                                {
                                    ( irregularMostrado.demandado.length !== 0 || ( administrador || coordinador ) )
                                    &&
                                        <>
                                            <Grid size={{ md: 4, xs: 12 }}>
                                                <Typography variant='subtitle2' color='text.secondary'>
                                                    Demandado(s):
                                                </Typography>
                                            </Grid>

                                            <Grid size={{ md: 7, xs: 10 }}>
                                                {
                                                    irregularMostrado.demandado?.map( (element, i) => (
                                                        <Typography key={ i } variant='subtitle2'>
                                                            { element }
                                                        </Typography>
                                                    ))
                                                }
                                            </Grid>

                                            <Grid size={{ md: 1, xs: 2 }}>
                                                {
                                                    ( administrador || coordinador )
                                                    &&
                                                        <Tooltip title='Editar partes'>
                                                            <IconButton
                                                                size='small'
                                                                color='primary'
                                                                onClick={ () => setOpenDialogPartesIrregular( true ) }
                                                            >
                                                                <EditIcon fontSize='small' />
                                                            </IconButton>
                                                        </Tooltip>
                                                }
                                            </Grid>
                                        </>
                                }

                            </Grid>

                        </CardContent>

                    </Card>
            }

            {
                esAmparo
                &&
                    <Card>

                        <CardHeader
                            sx={{
                                backgroundColor: (theme) => theme.palette.primary.main,
                                py: 2,
                                '& .MuiCardHeader-action': { alignSelf: 'center', margin: 0 },
                            }}
                            subheader={<Typography variant='subtitle1' fontWeight={500} sx={{ color: 'white' }}> Información del amparo </Typography>}
                            action={
                                <Stack direction='row' spacing={1.5} alignItems='center'>

                                    {
                                        ( administrador || coordinador )
                                        &&
                                            <Tooltip title='Editar amparo'>
                                                <IconButton
                                                    onClick={ () => setOpenEditarAmparo( true ) }
                                                    sx={{
                                                        color: 'white',
                                                        bgcolor: 'rgba(255, 255, 255, 0.15)',
                                                        '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.3)' },
                                                    }}
                                                >
                                                    <EditIcon />
                                                </IconButton>
                                            </Tooltip>
                                    }

                                    {
                                        amparoMostrado.estatus
                                        &&
                                            <ChipEstatus
                                                label={ 'Estatus' }
                                                value={ amparoMostrado.estatus }
                                                color={ coloresEstatusEtapa[ amparoMostrado.estatus ] ?? '#757575' }
                                            />
                                    }

                                </Stack>
                            }
                        />

                        <CardContent>

                            <Grid container rowSpacing={3} columnSpacing={1}>

                                <Grid size={{ md: 4, xs: 12 }}>
                                    <Typography variant='subtitle2' color='text.secondary'>
                                        Número auxiliar de amparo:
                                    </Typography>
                                </Grid>

                                <Grid size={{ md: 8, xs: 12 }}>
                                    <Typography variant='subtitle2'>
                                        { amparoMostrado.numeroAmparo }
                                    </Typography>
                                </Grid>

                                {
                                    amparoMostrado.numeroAmparoOrigen
                                    &&
                                        <>
                                            <Grid size={{ md: 4, xs: 12 }}>
                                                <Typography variant='subtitle2' color='text.secondary'>
                                                    Número de amparo de origen:
                                                </Typography>
                                            </Grid>

                                            <Grid size={{ md: 8, xs: 12 }}>
                                                <Typography variant='subtitle2'>
                                                    { amparoMostrado.numeroAmparoOrigen }
                                                </Typography>
                                            </Grid>
                                        </>
                                }

                                {
                                    amparoMostrado.numeroExpediente
                                    &&
                                        <>
                                            <Grid size={{ md: 4, xs: 12 }}>
                                                <Typography variant='subtitle2' color='text.secondary'>
                                                    Número de expediente:
                                                </Typography>
                                            </Grid>

                                            <Grid size={{ md: 8, xs: 12 }}>
                                                <Typography variant='subtitle2'>
                                                    { amparoMostrado.numeroExpediente }
                                                </Typography>
                                            </Grid>
                                        </>
                                }

                                {
                                    amparoMostrado.tipoAmparo
                                    &&
                                        <>
                                            <Grid size={{ md: 4, xs: 12 }}>
                                                <Typography variant='subtitle2' color='text.secondary'>
                                                    Tipo de amparo:
                                                </Typography>
                                            </Grid>

                                            <Grid size={{ md: 8, xs: 12 }}>
                                                <Typography variant='subtitle2'>
                                                    { amparoMostrado.tipoAmparo }
                                                </Typography>
                                            </Grid>
                                        </>
                                }

                                {
                                    amparoMostrado.fecha
                                    &&
                                        <>
                                            <Grid size={{ md: 4, xs: 12 }}>
                                                <Typography variant='subtitle2' color='text.secondary'>
                                                    Fecha del amparo:
                                                </Typography>
                                            </Grid>

                                            <Grid size={{ md: 8, xs: 12 }}>
                                                <Typography variant='subtitle2'>
                                                    { moment( amparoMostrado.fecha ).format( 'DD-MM-YYYY HH:mm a' ) }
                                                </Typography>
                                            </Grid>
                                        </>
                                }

                                {
                                    amparoMostrado.fechaInformePrevio
                                    &&
                                        <>
                                            <Grid size={{ md: 4, xs: 12 }}>
                                                <Typography variant='subtitle2' color='text.secondary'>
                                                    Fecha de informe previo:
                                                </Typography>
                                            </Grid>

                                            <Grid size={{ md: 8, xs: 12 }}>
                                                <Typography variant='subtitle2'>
                                                    { moment( amparoMostrado.fechaInformePrevio ).format( 'DD-MM-YYYY HH:mm a' ) }
                                                </Typography>
                                            </Grid>
                                        </>
                                }

                                {
                                    amparoMostrado.fechaInformeJustificado
                                    &&
                                        <>
                                            <Grid size={{ md: 4, xs: 12 }}>
                                                <Typography variant='subtitle2' color='text.secondary'>
                                                    Fecha de informe justificado:
                                                </Typography>
                                            </Grid>

                                            <Grid size={{ md: 8, xs: 12 }}>
                                                <Typography variant='subtitle2'>
                                                    { moment( amparoMostrado.fechaInformeJustificado ).format( 'DD-MM-YYYY HH:mm a' ) }
                                                </Typography>
                                            </Grid>
                                        </>
                                }

                                {
                                    amparoMostrado.fechaInterpone
                                    &&
                                        <>
                                            <Grid size={{ md: 4, xs: 12 }}>
                                                <Typography variant='subtitle2' color='text.secondary'>
                                                    Fecha de interposición:
                                                </Typography>
                                            </Grid>

                                            <Grid size={{ md: 8, xs: 12 }}>
                                                <Typography variant='subtitle2'>
                                                    { moment( amparoMostrado.fechaInterpone ).format( 'DD-MM-YYYY HH:mm a' ) }
                                                </Typography>
                                            </Grid>
                                        </>
                                }

                                {
                                    amparoMostrado.fechaResolucion
                                    &&
                                        <>
                                            <Grid size={{ md: 4, xs: 12 }}>
                                                <Typography variant='subtitle2' color='text.secondary'>
                                                    Fecha de resolución:
                                                </Typography>
                                            </Grid>

                                            <Grid size={{ md: 8, xs: 12 }}>
                                                <Typography variant='subtitle2'>
                                                    { moment( amparoMostrado.fechaResolucion ).format( 'DD-MM-YYYY HH:mm a' ) }
                                                </Typography>
                                            </Grid>
                                        </>
                                }

                                {
                                    amparoMostrado.autoridadAmparo
                                    &&
                                        <>
                                            <Grid size={{ md: 4, xs: 12 }}>
                                                <Typography variant='subtitle2' color='text.secondary'>
                                                    Autoridad del amparo:
                                                </Typography>
                                            </Grid>

                                            <Grid size={{ md: 8, xs: 12 }}>
                                                <Typography variant='subtitle2'>
                                                    { amparoMostrado.autoridadAmparo }
                                                </Typography>
                                            </Grid>
                                        </>
                                }

                                {
                                    amparoMostrado.sentidoAmparo
                                    &&
                                        <>
                                            <Grid size={{ md: 4, xs: 12 }}>
                                                <Typography variant='subtitle2' color='text.secondary'>
                                                    Sentido del amparo:
                                                </Typography>
                                            </Grid>

                                            <Grid size={{ md: 8, xs: 12 }}>
                                                <Typography variant='subtitle2'>
                                                    { amparoMostrado.sentidoAmparo }
                                                </Typography>
                                            </Grid>
                                        </>
                                }

                                {
                                    amparoMostrado.juez
                                    &&
                                        <>
                                            <Grid size={{ md: 4, xs: 12 }}>
                                                <Typography variant='subtitle2' color='text.secondary'>
                                                    Juez:
                                                </Typography>
                                            </Grid>

                                            <Grid size={{ md: 8, xs: 12 }}>
                                                <Typography variant='subtitle2'>
                                                    { amparoMostrado.juez }
                                                </Typography>
                                            </Grid>
                                        </>
                                }

                                {
                                    amparoMostrado.secretario
                                    &&
                                        <>
                                            <Grid size={{ md: 4, xs: 12 }}>
                                                <Typography variant='subtitle2' color='text.secondary'>
                                                    Secretario(a):
                                                </Typography>
                                            </Grid>

                                            <Grid size={{ md: 8, xs: 12 }}>
                                                <Typography variant='subtitle2'>
                                                    { amparoMostrado.secretario }
                                                </Typography>
                                            </Grid>
                                        </>
                                }

                                <Grid size={{ md: 4, xs: 12 }}>
                                    <Typography variant='subtitle2' color='text.secondary'>
                                        Juzgado de origen:
                                    </Typography>
                                </Grid>

                                <Grid size={{ md: 8, xs: 12 }}>
                                    <Typography variant='subtitle2'>
                                        { amparoMostrado.juzgadoOrigen }
                                    </Typography>
                                </Grid>

                                {
                                    ( amparoMostrado.partes.length !== 0 || ( administrador || coordinador ) )
                                    &&
                                        <>
                                            <Grid size={{ md: 4, xs: 12 }}>
                                                <Typography variant='subtitle2' color='text.secondary'>
                                                    Partes del amparo:
                                                </Typography>
                                            </Grid>

                                            <Grid size={{ md: 7, xs: 10 }}>

                                                <Stack divider={ <Divider /> } spacing={2}>
                                                    {
                                                        amparoMostrado.partes?.map( (element, i) => (
                                                            <Box
                                                                key={ i }
                                                                sx={{
                                                                    display: 'flex',
                                                                    flexDirection: 'column',
                                                                    alignItems: 'flex-start',
                                                                    gap: 0.5,
                                                                }}
                                                            >
                                                                <Typography variant='subtitle2' fontWeight={600}>
                                                                    { element.parte }
                                                                </Typography>

                                                                <Chip
                                                                    label={ element.tipoParte }
                                                                    size='small'
                                                                    sx={{
                                                                        fontSize: 11,
                                                                        borderRadius: '99px',
                                                                        backgroundColor: 'action.hover',
                                                                        border: '1px solid',
                                                                        borderColor: 'divider',
                                                                    }}
                                                                />
                                                            </Box>
                                                        ))
                                                    }
                                                </Stack>

                                            </Grid>

                                            <Grid size={{ md: 1, xs: 2 }}>
                                                {
                                                    ( administrador || coordinador )
                                                    &&
                                                        <Tooltip title='Editar partes'>
                                                            <IconButton
                                                                size='small'
                                                                color='primary'
                                                                onClick={ () => setOpenDialogPartesAmparo( true ) }
                                                            >
                                                                <EditIcon fontSize='small' />
                                                            </IconButton>
                                                        </Tooltip>
                                                }
                                            </Grid>
                                        </>
                                }

                            </Grid>

                        </CardContent>

                    </Card>
            }

            <Dialog
                fullWidth
                maxWidth='sm'
                open={ openConfirmarEstatus }
                onClose={ () => !loadingEstatus && setOpenConfirmarEstatus( false ) }
            >

                <DialogTitle>Confirmación</DialogTitle>

                <DialogContent>

                    <DialogContentText>
                        { autorizadoSeleccionado?.activo ? '¿Desea desactivar la autorización de' : '¿Desea activar de nueva cuenta la autorización de' } <strong>{ autorizadoSeleccionado?.nombre } { autorizadoSeleccionado?.apellido1 } { autorizadoSeleccionado?.apellido2 }</strong> para consultar este expediente?
                    </DialogContentText>

                </DialogContent>

                <DialogActions>

                    <Button color='secondary' onClick={ () => setOpenConfirmarEstatus( false ) } disabled={ loadingEstatus }>Cancelar</Button>
                    <Button variant='contained' loading={ loadingEstatus } onClick={ handleCambiarEstatus }>Aceptar</Button>

                </DialogActions>

            </Dialog>

            <Dialog
                fullWidth
                maxWidth='sm'
                open={ openAudiencias }
                onClose={ () => setOpenAudiencias( false ) }
            >

                <DialogTitle>Audiencias</DialogTitle>

                <DialogContent>

                    {
                        loadingAudiencias
                        &&
                            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                                <CircularProgress size={ 38 } />
                            </Box>
                    }

                    {
                        !loadingAudiencias
                        &&
                            <Stack spacing={1.5}>
                                {
                                    audiencias.map( ({ tipoAudiencia, estatus, fecha }, i) => (
                                        <Box
                                            key={ i }
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'space-between',
                                                gap: 2,
                                                p: 2,
                                                borderRadius: 2,
                                                border: '1px solid',
                                                borderColor: 'divider',
                                            }}
                                        >
                                            <Box>
                                                <Typography variant='h6' fontWeight={600}>
                                                    { tipoAudiencia }
                                                </Typography>

                                                <Stack direction='row' spacing={3} mt={0.5}>

                                                    <Typography variant='subtitle2' color='text.secondary'>
                                                        { moment( fecha ).format( 'DD-MM-YYYY' ) }
                                                    </Typography>

                                                    <Typography variant='subtitle2' color='text.secondary'>
                                                        { moment( fecha ).format( 'hh:mm a' ) }
                                                    </Typography>

                                                </Stack>
                                            </Box>

                                            <Chip
                                                label={ <Typography variant='subtitle2' fontWeight={600} sx={{ fontSize: '0.85rem' }}>{ estatus }</Typography> }
                                                size='small'
                                                sx={{
                                                    backgroundColor: coloresEstatusAudiencia[ estatus ] ?? '#757575',
                                                    color: 'white',
                                                    height: 28,
                                                    py: 2,
                                                    px: 1,
                                                }}
                                            />

                                        </Box>
                                    ))
                                }
                            </Stack>
                    }

                </DialogContent>

                <DialogActions>
                    <Button color='secondary' onClick={ () => setOpenAudiencias( false ) }>Cerrar</Button>
                </DialogActions>

            </Dialog>

            <Dialog
                fullWidth
                maxWidth='sm'
                open={ openEtapas }
                onClose={ () => setOpenEtapas( false ) }
            >

                <DialogTitle>Etapas</DialogTitle>

                <DialogContent>

                    {
                        loadingEtapas
                        &&
                            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                                <CircularProgress size={ 38 } />
                            </Box>
                    }

                    {
                        !loadingEtapas
                        &&
                            <Stack spacing={1.5}>
                                {
                                    etapas.length === 0
                                    &&
                                        <Typography variant='subtitle2' color='text.secondary' textAlign='center' py={4}>
                                            No se encontró ninguna etapa
                                        </Typography>
                                }

                                {
                                    etapas.map( ({ etapa, subEtapa, fecha }, i) => (
                                        <Box
                                            key={ i }
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'space-between',
                                                gap: 2,
                                                p: 2,
                                                borderRadius: 2,
                                                border: '1px solid',
                                                borderColor: 'divider',
                                            }}
                                        >
                                            <Box>
                                                <Typography variant='subtitle2' fontWeight={600}>
                                                    { subEtapa ? `${ etapa } (${ subEtapa })` : etapa }
                                                </Typography>
                                            </Box>

                                            <Typography variant='subtitle2' color='text.secondary'>
                                                { moment( fecha ).format( 'DD-MM-YYYY HH:mm a' ) }
                                            </Typography>

                                        </Box>
                                    ))
                                }
                            </Stack>
                    }

                </DialogContent>

                <DialogActions>
                    <Button color='secondary' onClick={ () => setOpenEtapas( false ) }>Cerrar</Button>
                </DialogActions>

            </Dialog>

            <Dialog
                fullWidth
                maxWidth='sm'
                open={ openAutorizados }
                onClose={ () => setOpenAutorizados( false ) }
            >

                <DialogTitle>Expedientes autorizados</DialogTitle>

                <DialogContent>

                    <Stack spacing={1.5}>
                        {
                            autorizados.map( (autorizado, i) => (
                                <Box
                                    key={ i }
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        gap: 2,
                                        p: 2,
                                        borderRadius: 2,
                                        border: '1px solid',
                                        borderColor: 'divider',
                                    }}
                                >
                                    <Box>
                                        <Typography variant='subtitle2' fontWeight={600}>
                                            { autorizado.nombre } { autorizado.apellido1 } { autorizado.apellido2 }
                                        </Typography>

                                        <Stack direction='row' spacing={3} mt={0.5}>

                                            <Stack direction='row' spacing={0.5} alignItems='center'>
                                                <Typography variant='caption' color='text.secondary'>
                                                    { autorizado.correoElectronico }
                                                </Typography>
                                            </Stack>

                                            <Stack direction='row' spacing={0.5} alignItems='center'>
                                                <Typography variant='caption' color='text.secondary'>
                                                    { autorizado.telefonoCelular }
                                                </Typography>
                                            </Stack>

                                        </Stack>
                                    </Box>

                                    <Stack direction='row' spacing={1} alignItems='center'>

                                        <Chip
                                            label={ autorizado.activo ? 'Activo' : 'Inactivo' }
                                            color={ 'primary' }
                                            size='small'
                                        />

                                        {
                                            ( administrador || coordinador )
                                            &&
                                                <Tooltip title={ autorizado.activo ? 'Desactivar autorización' : 'Activar autorización' }>
                                                    <IconButton
                                                        size='small'
                                                        color={ 'primary' }
                                                        onClick={ () => handleAbrirCambioEstatus( autorizado ) }
                                                    >
                                                        { autorizado.activo ? <BlockIcon fontSize='small' /> : <CheckCircleIcon fontSize='small' /> }
                                                    </IconButton>
                                                </Tooltip>
                                        }

                                    </Stack>
                                </Box>
                            ))
                        }
                    </Stack>

                </DialogContent>

                <DialogActions>
                    <Button color='secondary' onClick={ () => setOpenAutorizados( false ) }>Cerrar</Button>
                </DialogActions>

            </Dialog>

            <DialogAutorizarExpedienteVirtual
                openDialog={ openAutorizarExpedienteVirtual }
                idExpediente={ expedienteMostrado.idExpediente }
                handleCloseDialog={ () => setOpenAutorizarExpedienteVirtual( false ) }
                onAutorizado={ (nuevoAutorizado) => setAutorizados( prev => {

                    const esMismoSolicitante = (autorizado: ExpedienteAutorizadoProps) =>
                        ( nuevoAutorizado.correoElectronico !== '' && autorizado.correoElectronico === nuevoAutorizado.correoElectronico ) ||
                        ( nuevoAutorizado.telefonoCelular !== '' && autorizado.telefonoCelular === nuevoAutorizado.telefonoCelular );

                    return prev.some( esMismoSolicitante )
                        ? prev.map( (autorizado) => esMismoSolicitante( autorizado ) ? nuevoAutorizado : autorizado )
                        : [ ...prev, nuevoAutorizado ];

                }) }
            />

            <DialogEditarExpediente
                openDialog={ openEditarExpediente }
                handleCloseDialog={ () => setOpenEditarExpediente( false ) }
                expediente={ expedienteMostrado }
                onGuardar={ setExpedienteMostrado }
            />

            <DialogPartesExpediente
                idExpediente={ expedienteMostrado.idExpediente }
                openDialog={ openDialogPartes }
                handleCloseDialog={ () => setOpenDialogPartes( false ) }
                onGuardar={ (actor, demandado) => setExpedienteMostrado( prev => ({ ...prev, actor, demandado }) ) }
            />
            
            <DialogPrestacionesExpediente
                idExpediente={ expedienteMostrado.idExpediente }
                idTipoJuicio={ expedienteMostrado.idTipoJuicio }
                openDialog={ openDialogPrestaciones }
                handleCloseDialog={ () => setOpenDialogPrestaciones( false ) }
                onGuardar={ (prestaciones) => setExpedienteMostrado( prev => ({ ...prev, prestaciones }) ) }
            />

            <DialogAgregarCitacionSentencia
                openDialog={ openCitacionSentencia }
                handleCloseDialog={ () => setOpenCitacionSentencia( false ) }
                expediente={ expedienteMostrado }
            />

            <DialogEditarExhortoRecibido
                openDialog={ openEditarExhorto }
                handleCloseDialog={ () => setOpenEditarExhorto( false ) }
                exhorto={ exhortoMostrado }
                onGuardar={ setExhortoMostrado }
            />

            <DialogPartesExhortoRecibido
                idExhortoRecibido={ exhortoMostrado.idExhortoRecibido }
                openDialog={ openDialogPartesExhorto }
                handleCloseDialog={ () => setOpenDialogPartesExhorto( false ) }
                onGuardar={ (actor, demandado) => setExhortoMostrado( prev => ({ ...prev, actor, demandado }) ) }
            />

            <DialogEditarIrregular
                openDialog={ openEditarIrregular }
                handleCloseDialog={ () => setOpenEditarIrregular( false ) }
                irregular={ irregularMostrado }
                onGuardar={ setIrregularMostrado }
            />

            <DialogPartesIrregular
                idIrregular={ irregularMostrado.idIrregular }
                openDialog={ openDialogPartesIrregular }
                handleCloseDialog={ () => setOpenDialogPartesIrregular( false ) }
                onGuardar={ (actor, demandado) => setIrregularMostrado( prev => ({ ...prev, actor, demandado }) ) }
            />

            <DialogEditarAmparo
                openDialog={ openEditarAmparo }
                handleCloseDialog={ () => setOpenEditarAmparo( false ) }
                amparo={ amparoMostrado }
                onGuardar={ setAmparoMostrado }
            />

            <DialogPartesAmparo
                idAmparo={ amparoMostrado.idAmparo }
                openDialog={ openDialogPartesAmparo }
                handleCloseDialog={ () => setOpenDialogPartesAmparo( false ) }
                onGuardar={ (partes) => setAmparoMostrado( prev => ({ ...prev, partes: partes.map( (p) => ({ parte: p.persona, tipoParte: p.tipoParte }) ) }) ) }
            />

        </Stack>
    );
}
